import React, { FC, useEffect, useRef, useState } from 'react';
import { useTrainingInitLazyQuery } from '~/data';
import { select } from '~/lib/select';
import { NotFound } from '~/ui/not-found/NotFound';
import { Spinner } from '~/ui/spinner/Spinner';
import { useTrainingParams } from '../useTrainingParams';
import { processData } from './processData';
import { TrainingState } from './TrainingState';
import { useTrainingStore } from './useTrainingStore';

const selector = (s: TrainingState) =>
  select(s, 'textLocale', 'mediumLocale', 'subtitleLocale');

export const InitTrainingState: FC = ({ children }) => {
  const { learnerTrainingId } = useTrainingParams();
  const [getTrainingInit, { data, refetch, error }] =
    useTrainingInitLazyQuery();
  const [done, setDone] = useState(false);
  const { textLocale, mediumLocale, subtitleLocale } =
    useTrainingStore(selector);

  useEffect(() => {
    // This will automatically call the API back when the language is changed,
    // ensuring that the navigation menu is updated.
    if (refetch && data?.learnerTraining) {
      if (
        window.location.pathname.split('/').pop() != 'onboarding' &&
        (data.learnerTraining.textLocale != textLocale ||
          data.learnerTraining.mediumLocale != mediumLocale ||
          data.learnerTraining.subtitleLocale != subtitleLocale)
      ) {
        document.location.reload();
      }

      if (
        data.learnerTraining.textLocale != textLocale ||
        data.learnerTraining.mediumLocale != mediumLocale ||
        data.learnerTraining.subtitleLocale != subtitleLocale
      ) {
        refetch();
      }
    }
  }, [textLocale, mediumLocale, subtitleLocale]);

  // Call the API when the learner training id is present or updated
  useEffect(() => {
    if (!learnerTrainingId) return;
    getTrainingInit({ variables: { learnerTrainingUuid: learnerTrainingId } });
  }, [learnerTrainingId]);

  // Process the API data and update the store when data changes
  useEffect(() => {
    let unloaded = false;
    if (!data) return;
    useTrainingStore.setState(processData(data));
    setTimeout(() => {
      if (!unloaded) setDone(true);
    });
    return () => {
      unloaded = true;
    };
  }, [data]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statusCode = (error?.networkError as any)?.statusCode;
  if (statusCode === 401 || statusCode === 404) return <NotFound />;

  if (!done) return <Spinner />;

  if (!children) return null;

  return <>{children}</>;
};
